import { useSiteContent } from "@/context/SiteContentContext";
import { useT } from "@/context/LanguageContext";
import { ADMIN_TOKEN_KEY } from "@/lib/api";

/**
 * Full-page maintenance overlay.
 *
 * When `site_settings.maintenance_mode` is true, this covers the entire
 * storefront with a warm, on-brand message so returning customers
 * don't see broken pages while the boutique is mid-migration or in
 * the middle of a launch prep.
 *
 * Bypassed for two audiences:
 *   1. Anyone with an `ADMIN_TOKEN_KEY` in localStorage — the owner can
 *      keep previewing the site while it's officially "closed".
 *   2. Admins are already routed to `/admin` (bare, no chrome) via
 *      App.js so this component never mounts for them anyway.
 *
 * Copy is admin-editable per-locale via /admin → Site Status →
 * Maintenance Mode. Falls back to compiled defaults per language when
 * an override is empty so the overlay always reads well.
 */
const DEFAULT_MESSAGES = {
  en: "We're brewing something new. Check back shortly — thank you for your patience.",
  pt: "Estamos a preparar novidades. Voltamos em breve — obrigado pela paciência.",
  es: "Estamos preparando novedades. Volvemos pronto — gracias por tu paciencia.",
};

const DEFAULT_TITLES = {
  en: "Back soon",
  pt: "Voltamos em breve",
  es: "Volvemos pronto",
};

const MaintenanceOverlay = () => {
  const { settings } = useSiteContent();
  const { lang } = useT();

  if (!settings?.maintenance_mode) return null;

  // Admin bypass — anyone with a valid-looking admin token in
  // localStorage still gets the full site so the owner can preview.
  if (typeof window !== "undefined" && window.localStorage?.getItem(ADMIN_TOKEN_KEY)) {
    return null;
  }

  const overrides = settings?.maintenance_message || {};
  const message = (overrides[lang] || "").trim() || DEFAULT_MESSAGES[lang] || DEFAULT_MESSAGES.en;
  const title = DEFAULT_TITLES[lang] || DEFAULT_TITLES.en;

  return (
    <div
      data-testid="maintenance-overlay"
      className="fixed inset-0 z-[9999] bg-[var(--gl-orange)] flex flex-col items-center justify-center px-6 text-center"
    >
      <div className="font-serif-display text-6xl md:text-8xl tracking-tight text-[#241C18] mb-6">
        GOYA <span className="italic font-normal">Lisboa</span>
      </div>
      <div className="font-serif-display text-3xl md:text-4xl text-[#241C18] mb-4">
        {title}
      </div>
      <p className="max-w-md font-sans-clean text-base md:text-lg text-[#241C18]/85 leading-relaxed">
        {message}
      </p>
      <div className="mt-10 font-badge text-[10px] tracking-[0.3em] uppercase text-[#241C18]/60">
        goyalisboa.pt
      </div>
    </div>
  );
};

export default MaintenanceOverlay;
